// Web Push subscription.
//
// This is the piece that was missing. The service worker already handles `push`
// and `notificationclick` (src/pwa/sw.ts), the backend already stores
// subscriptions and sends them — but nothing ever called pushManager.subscribe(),
// so a worker who granted notification permission was never reachable. Granting
// permission is not subscribing: the browser only starts delivering once a
// subscription exists and the server knows about it.
//
// A worker who cannot be reached is a worker who misses a job, so every failure
// path here is explicit and none of them throws into the UI.

import type { DocumentNode } from 'graphql';

import {
  REGISTER_PUSH_SUBSCRIPTION,
  UNREGISTER_PUSH_SUBSCRIPTION,
} from '@/graphql/operations';

/**
 * The only thing this module needs from Apollo: a way to run a mutation.
 *
 * Typed structurally rather than as ApolloClient<NormalizedCacheObject>,
 * because useApolloClient() hands back ApolloClient<object> and the two
 * generics are not assignable. Depending on the method instead of the class
 * keeps this working whichever the caller has.
 */
export interface MutationRunner {
  mutate(options: { mutation: DocumentNode; variables?: Record<string, unknown> }): Promise<unknown>;
}

/**
 * The VAPID application server key, baked in at build time.
 *
 * It is the public half of the keypair — safe to ship to a browser, and the
 * browser cannot subscribe without it.
 */
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PushSetupResult =
  | 'subscribed'
  | 'unsupported'
  | 'permission-denied'
  | 'not-configured'
  | 'failed';

/**
 * Whether this browser can do Web Push at all.
 *
 * iOS only exposes PushManager once the PWA is installed to the home screen,
 * so this is false in mobile Safari and true in the same app after "Add to
 * Home Screen". That is a real state, not an error — the UI should tell the
 * worker to install rather than showing a failure.
 */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Whether the app is running as an installed PWA rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari predates display-mode and uses a non-standard flag.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * base64url → Uint8Array.
 *
 * applicationServerKey must be raw bytes; handing it the base64 string fails
 * with an opaque InvalidCharacterError.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Read a subscription key as the base64url string the backend stores. */
function keyToBase64(sub: PushSubscription, name: 'p256dh' | 'auth'): string {
  const key = sub.getKey(name);
  if (!key) return '';
  return window
    .btoa(String.fromCharCode(...new Uint8Array(key)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Subscribe this device to push and tell the backend about it.
 *
 * Safe to call repeatedly: pushManager.subscribe() returns the existing
 * subscription when one is already active, and registerPushSubscription is
 * idempotent on endpoint. Call it after the worker grants permission AND on
 * every app start where permission is already granted — a subscription can be
 * dropped by the browser at any time, and the only way to notice is to re-assert
 * it.
 *
 * It never throws. Notifications are an enhancement; a failure here must not
 * take down the screen the worker is trying to use.
 */
export async function subscribeToPush(
  client: MutationRunner,
): Promise<PushSetupResult> {
  if (!isPushSupported()) return 'unsupported';
  if (!VAPID_PUBLIC_KEY) {
    // Shipping without the key is a deploy mistake, not a user problem. Say so
    // loudly in the console rather than failing silently forever.
    console.error('[PushService] VITE_VAPID_PUBLIC_KEY is not set; push cannot work');
    return 'not-configured';
  }
  if (Notification.permission !== 'granted') return 'permission-denied';

  try {
    const registration = await navigator.serviceWorker.ready;

    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        // Required by every browser: a push that does not show a notification
        // is not allowed, and silently drops the subscription if attempted.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const p256dh = keyToBase64(sub, 'p256dh');
    const auth = keyToBase64(sub, 'auth');
    if (!p256dh || !auth) {
      console.error('[PushService] subscription is missing its encryption keys');
      return 'failed';
    }

    await client.mutate({
      mutation: REGISTER_PUSH_SUBSCRIPTION,
      variables: {
        input: {
          endpoint: sub.endpoint,
          p256dh,
          auth,
          userAgent: navigator.userAgent,
        },
      },
    });

    return 'subscribed';
  } catch (err) {
    console.error('[PushService] subscribe failed:', err);
    return 'failed';
  }
}

/**
 * Ask for permission if it has not been decided, then subscribe.
 *
 * Only call this from a real user gesture. A permission prompt fired on page
 * load is the fastest way to get permanently denied, and 'denied' cannot be
 * undone from script — the worker has to change it in browser settings.
 */
export async function requestPermissionAndSubscribe(
  client: MutationRunner,
): Promise<PushSetupResult> {
  if (!isPushSupported()) return 'unsupported';

  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return 'permission-denied';
  }
  return subscribeToPush(client);
}

/**
 * Re-assert the subscription on app start when permission is already granted.
 *
 * Cheap, and it repairs the two states that otherwise leave a worker silently
 * unreachable: a browser that dropped the subscription, and a user whose row
 * was deleted server-side after repeated delivery failures.
 */
export async function ensurePushSubscription(
  client: MutationRunner,
): Promise<PushSetupResult> {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission !== 'granted') return 'permission-denied';
  return subscribeToPush(client);
}

/**
 * Unsubscribe this device and tell the backend to forget it.
 *
 * Used on logout: the next person to use the phone must not receive the
 * previous worker's job notifications.
 */
export async function unsubscribeFromPush(
  client: MutationRunner,
): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return;

    const { endpoint } = sub;
    await sub.unsubscribe();
    await client.mutate({
      mutation: UNREGISTER_PUSH_SUBSCRIPTION,
      variables: { endpoint },
    });
  } catch (err) {
    console.error('[PushService] unsubscribe failed:', err);
  }
}
